import { Trans } from '@lingui/react/macro';
import { type ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTitle, TooltipTrigger } from '@/components/Tooltip';
import { Link } from '@/components/typography';
import { type NumberFormatOptionsType, numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

import {
  type EventLogParamPrimitiveType,
  type EventLogParamReferenceType,
  type EventLogParamReferenceValues,
  type EventLogParamType,
} from './eventLogType';

const isEventLogParamPrimitive = (param: EventLogParamType): param is EventLogParamPrimitiveType =>
  param.type === 'strn' || param.type === 'nmbr';

const typeToPath: Record<EventLogParamReferenceValues, (id?: string) => string | undefined> = {
  user: ROUTES.userById,
  oprv: ROUTES.integrationById,
  pixl: ROUTES.pixelById,
  audi: ROUTES.audienceById,
  vndr: ROUTES.vendorById,
  chnl: ROUTES.channelById,
  vspd: () => ROUTES.configureVendors.path,
  cmap: () => ROUTES.configureChannels.path,
};

const reactReplaceText = (
  text: string,
  regex: RegExp,
  formatToken: (token: string, index: number) => ReactNode,
) => {
  const textArray = text.split(regex);
  return textArray.map((text, index) => {
    if (text.match(regex)) {
      return formatToken(text.slice(1, -1), index);
    }
    return text;
  });
};

const createMessageLink = (
  param: EventLogParamReferenceType,
  getPathById: (id?: string) => string | undefined,
  index: number,
) => {
  if (param.isDeleted) {
    return (
      <Tooltip key={index}>
        <TooltipTrigger
          render={
            <Link variant="caption1" color="static-red" disabled>
              {param.value}
            </Link>
          }
        />
        <TooltipContent className="flex-col items-start">
          <TooltipTitle>
            <Trans>Deleted object</Trans>
          </TooltipTitle>
          <Trans>This object has been deleted</Trans>
        </TooltipContent>
      </Tooltip>
    );
  } else {
    return (
      <Link key={index} variant="caption1" to={getPathById(param.id)}>
        {param.value}
      </Link>
    );
  }
};

const formatNumberValue = (value: string | number, format?: string) => {
  const formatOptions: NumberFormatOptionsType = {
    isPercent: false,
    precision: 0,
    isCurrency: false,
    isMultiplier: false,
    compact: false,
  };

  if (format) {
    const numberType = format[0];
    const precisionStr = format.substring(1);

    if (numberType === '$') {
      formatOptions.isCurrency = true;
    }

    if (numberType === '%') {
      formatOptions.isPercent = true;
    }

    if (numberType === '#') {
      formatOptions.isCurrency = false;
      formatOptions.isPercent = false;
    }

    if (precisionStr.length) {
      formatOptions.precision = +precisionStr;
    }
  }

  return numberFormat(value, formatOptions);
};

export const hydrateEventLogMessage = (
  message: string,
  params: Record<string, EventLogParamType>,
  linkify = true,
): string | ReactNode => {
  const replacedText = reactReplaceText(message, /({[^}]+})/g, (token, index) => {
    const param = params[token];

    // If the param is not found or is null, return the key.
    if (!param || param.value === null) {
      return `{${token}}`;
    }

    if (isEventLogParamPrimitive(param)) {
      if (param.type === 'nmbr') {
        return formatNumberValue(param.value, param.format);
      }

      if (param.type === 'strn') {
        return param.value;
      }
    } else {
      // Else it's a reference and we'll be displaying a link to the object.
      const getPathById = typeToPath[param.type];
      if (!getPathById) {
        return param.value;
      }

      if (linkify) {
        return createMessageLink(param, getPathById, index);
      } else {
        return param.value;
      }
    }
  });

  if (!linkify) {
    return replacedText.join('');
  }
  return replacedText;
};
